import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { contactApi, contactRoleApi, customerApi, documentApi, orgApi } from "@/lib/entities";
import { tourApi } from "@/lib/tourEntities";
import { quoteApi, contractApi } from "@/lib/phase3Entities";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, PageShell } from "@/components/PageHeader";
import { SectionHeader } from "@/components/SectionHeader";
import { DemoChip } from "@/components/Chips";
import { RoleGate } from "@/components/RoleGate";
import { SetupProgress } from "@/components/SetupProgress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CustomerFormDialog } from "@/pages/customers/CustomerFormDialog";
import { ContactFormDialog } from "@/pages/contacts/ContactFormDialog";
import { TourFormDialog } from "@/pages/tours/TourFormDialog";
import { apiError } from "@/lib/errors";
import { Plus, Pencil, Route as RouteIcon } from "lucide-react";
import { ClientContactRelationshipsPanel } from "@/components/ClientContactRelationshipsPanel";

const READ_ROLES = ["super_admin", "leasing_sales", "ops_dispatch", "accounting", "maintenance"];
// Wave 4B-6 · gates are capability-based via can(...). All contact/tour/
// customer write actions are backed by canonical `perm(...)` deps on the
// backend routes; Admin auto-inherits every non-root permission.

const ROLE_LABEL = {
  tour_manager: "Tour Manager",
  production_manager: "Production Manager",
  business_manager: "Business Manager",
  personal_manager: "Personal Manager",
  road_manager: "Road Manager",
  transportation_coordinator: "Transportation Coordinator",
  artist_principal: "Artist / Principal",
  accountant: "Accountant",
  booking_agent: "Booking Agent",
  manager: "Manager",
  assistant: "Assistant",
  other: "Other",
};

export default function CustomerDetail() {
  const { id } = useParams();
  const { user, can } = useAuth();
  const [data, setData] = useState(null);
  const [docs, setDocs] = useState([]);
  const [activity, setActivity] = useState([]);
  const [contactsAll, setContactsAll] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [tours, setTours] = useState([]);
  const [rollup, setRollup] = useState({ quotes: [], contracts: [], loading: false });
  const [editOpen, setEditOpen] = useState(false);
  const [addContactOpen, setAddContactOpen] = useState(false);
  const [newTourOpen, setNewTourOpen] = useState(false);
  // RC10a · return-context restore. When a Contact-name link was
  // clicked from the Contacts tab we store {tab: "contacts", scrollY,
  // ts} in sessionStorage under `client:{id}:returnCtx`. On mount
  // (typically via browser Back) we hydrate the initial tab so the
  // user returns to the same panel with scroll position preserved.
  // NB: read + consume the sessionStorage entry synchronously in the
  // state initializer so `Tabs defaultValue` sees it on FIRST render.
  const [initialTab] = useState(() => {
    try {
      const raw = sessionStorage.getItem(`client:${id}:returnCtx`);
      if (!raw) return "overview";
      const ctx = JSON.parse(raw);
      sessionStorage.removeItem(`client:${id}:returnCtx`);
      if (
        typeof ctx?.tab === "string"
        && (Date.now() - (ctx.ts || 0)) < 5 * 60 * 1000
      ) {
        // Restore scroll after paint so the panel is mounted.
        window.setTimeout(() => {
          try { window.scrollTo({ top: ctx.scrollY || 0, behavior: "instant" }); }
          catch { /* older browsers: swallow */ }
        }, 80);
        return ctx.tab;
      }
    } catch { /* corrupt JSON or blocked storage — silent */ }
    return "overview";
  });

  const canEditCustomer = can("customers.edit");
  const canCreateTour = can("tours.create");
  const canAddContact = can("contacts.create");
  const canWrite = canEditCustomer || canCreateTour || canAddContact;
  // CC2 · canonical permission; Admin auto-inherits. Replaces the previous
  // hardcoded `user?.role === "super_admin"` gate.
  const canSeeActivity = can("customers.view_activity");

  const load = async () => {
    try {
      const c = await customerApi.get(id);
      setData(c);
      const d = await documentApi.list("customer", id).catch(() => []);
      setDocs(d);
      if (canSeeActivity) {
        customerApi
          .activity(id)
          .then(setActivity)
          .catch(() => setActivity([]));
      }
      const tRes = await tourApi
        .list({ customer_id: id, page_size: 100 })
        .catch(() => ({ results: [] }));
      const tourList = tRes.results || [];
      setTours(tourList);
      // Fan-out to per-tour quotes/contracts. Aggregate into a single rollup
      // shared by Quotes / Contracts / Billing tabs and the Overview
      // Quick Stats. Executed contracts drive Billing.
      loadRollup(tourList);
    } catch (e) {
      toast.error("Customer not found");
    }
  };

  const loadRollup = async (tourList) => {
    if (!tourList || tourList.length === 0) {
      setRollup({ quotes: [], contracts: [], loading: false });
      return;
    }
    setRollup((prev) => ({ ...prev, loading: true }));
    try {
      const [qResp, cResp] = await Promise.all([
        Promise.all(
          tourList.map((t) =>
            quoteApi.list(t.id).then(
              (rows) => (rows || []).map((r) => ({ ...r, _tour: t })),
              () => []
            )
          )
        ),
        Promise.all(
          tourList.map((t) =>
            contractApi.list(t.id).then(
              (rows) => (rows || []).map((r) => ({ ...r, _tour: t })),
              () => []
            )
          )
        ),
      ]);
      const quotes = qResp.flat();
      const contracts = cResp.flat();
      setRollup({ quotes, contracts, loading: false });
    } catch {
      setRollup({ quotes: [], contracts: [], loading: false });
    }
  };

  useEffect(() => {
    load();
    contactApi.list({ page_size: 200 }).then((d) => setContactsAll(d.results)).catch(() => {});
    orgApi.list({ page_size: 200 }).then((d) => setOrgs(d.results)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!data) {
    return (
      <PageShell>
        {/* RC4 · shared SectionHeader adoption landmark (sr-only until full visual migration lands progressively). */}
        <SectionHeader testid="page-section-landmark" className="sr-only">Client Detail</SectionHeader>
        <div className="text-steel text-sm">Loading customer…</div>
      </PageShell>
    );
  }

  // Phase A.2.b BLOCKER 2 · Primary is now derived from the canonical
  // client_contact_relationships.is_primary flag (see
  // /app/memory/phaseA2b_primary_inspection.md). We keep the derived
  // CROC-role payload around for the Overview card's read-only summary
  // but treat "has Primary Person" as any active CCR with is_primary=true.
  const primaryContacts = data.primary_contacts || [];
  const primaryPeopleCount = (data.people_primary_count ?? primaryContacts.length);
  const setupSteps = [
    { key: "name", label: "Client Name", complete: !!data.display_name },
    { key: "legal", label: "Legal Entity", complete: !!(data.legal_entity_text || data.legal_entity_organization_id) },
    { key: "contact", label: "Primary Person", complete: primaryPeopleCount > 0 },
    { key: "tour", label: "First Tour", complete: tours.length > 0 },
  ];

  return (
    <RoleGate permission="customers.view">
      <PageShell>
        <PageHeader
          testid="page-customer-detail"
          backTo={{ label: "Back to Clients", to: "/customers" }}
          title={
            <span className="inline-flex items-center gap-3">
              {data.display_name} {data.is_demo && <DemoChip />}
            </span>
          }
          subtitle={
            <span className="inline-flex items-center gap-3">
              {/* RC4 · ITEM 8 — customer_number chip hidden. Setup
                  progress remains. */}
              <SetupProgress steps={setupSteps} testid="customer-setup-progress" />
            </span>
          }
          right={
            <div className="flex items-center gap-2 flex-wrap justify-end w-full md:w-auto">
              {can("customers.edit") && (
                <button
                  data-testid="view-as-demo-client"
                  onClick={async () => {
                    try {
                      const { api } = await import("@/lib/api");
                      const r = await api.post("/portal/token", { customer_id: id });
                      const t = r.data.access_token;
                      window.open(`/portal?token=${encodeURIComponent(t)}`, "_blank");
                    } catch (e) {
                      toast.error(apiError(e));
                    }
                  }}
                  className="px-3 py-1.5 text-[12.5px] border border-touring-lime bg-touring-lime/20 text-carbon rounded-sm hover:bg-touring-lime/40"
                >
                  View as Demo Client →
                </button>
              )}
              {canWrite && (
                <>
                  {canAddContact && (
                  <button
                    data-testid="customer-add-contact-btn"
                    onClick={() => setAddContactOpen(true)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-[12.5px] border border-coach-silver text-carbon rounded-sm hover:bg-road-white"
                  >
                    <Plus size={13} /> Add Contact
                  </button>
                  )}
                  {canCreateTour && (
                  <button
                    data-testid="customer-new-tour-btn"
                    onClick={() => setNewTourOpen(true)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-[12.5px] border border-coach-silver text-carbon rounded-sm hover:bg-road-white"
                  >
                    <RouteIcon size={13} /> New Tour
                  </button>
                  )}
                  {canEditCustomer && (
                  <button
                    data-testid="customer-edit-btn"
                    onClick={() => setEditOpen(true)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-[12.5px] bg-coach-blue text-white rounded-sm hover:bg-deep-road-navy"
                  >
                    <Pencil size={13} /> Edit Client
                  </button>
                  )}
                </>
              )}
            </div>
          }
        />

        <Tabs defaultValue={initialTab} className="mt-2">
          <TabsList
            data-testid="customer-tabs"
            className="bg-white border border-coach-silver rounded-sm w-full justify-start overflow-x-auto"
          >
            {[
              ["overview", "Overview"],
              ["contacts", "Contacts"],
              ["tours", "Tours"],
              ["quotes", "Quotes"],
              ["contracts", "Contracts"],
              ["billing", "Billing"],
              ["documents", "Documents"],
              ["notes", "Notes"],
              ["activity", "Activity"],
            ].map(([k, l]) => (
              <TabsTrigger
                key={k}
                value={k}
                data-testid={`customer-tab-${k}`}
                className="data-[state=active]:bg-coach-blue data-[state=active]:text-white"
              >
                {l}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="overview" className="mt-4">
            <div className="grid md:grid-cols-2 gap-4">
              <Card title="Linked Organizations">
                <OrgRow label="Display" text={data.display_org_text} org={data.display_organization} />
                <OrgRow label="Legal Entity" text={data.legal_entity_text} org={data.legal_entity_organization} />
                <OrgRow label="Billing" text={data.billing_org_text} org={data.billing_organization} />
              </Card>
              <Card title="Quick Stats">
                <QuickStats tours={tours} rollup={rollup} />
              </Card>
              <Card title="Recent Documents">
                {docs.length === 0 && (
                  <div className="text-[12px] text-steel">No documents on this customer yet.</div>
                )}
                {docs.slice(0, 5).map((d) => (
                  <div key={d.id} className="py-1.5 border-b border-coach-silver/50 last:border-0 flex justify-between">
                    <span className="text-[13px] text-carbon">{d.title}</span>
                    <span className="text-[11.5px] text-steel uppercase tracking-wide">{d.document_type}</span>
                  </div>
                ))}
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="contacts" className="mt-4">
            <div className="space-y-4">
              <ClientContactRelationshipsPanel mode="client" customerId={id} />
            </div>
          </TabsContent>

          <TabsContent value="documents" className="mt-4">
            <DocumentsPanel customerId={id} docs={docs} onRefresh={load} canWrite={canWrite} />
          </TabsContent>

          <TabsContent value="notes" className="mt-4">
            <NotesLedgerPanel customerId={id} canWrite={canEditCustomer} />
          </TabsContent>

          <TabsContent value="activity" className="mt-4">
            <ActivityLedgerPanel activity={activity} canSeeActivity={canSeeActivity} />
          </TabsContent>

          <TabsContent value="tours" className="mt-4">
            <div className="bg-white border border-coach-silver rounded-md">
              <div className="px-4 py-3 border-b border-coach-silver flex items-center justify-between">
                <div className="text-[13px] text-carbon">
                  {tours.length === 0 ? (
                    <span className="text-steel italic">No tours yet.</span>
                  ) : (
                    <>
                      <span className="font-medium">{tours.length}</span>{" "}
                      <span className="text-steel">tour{tours.length === 1 ? "" : "s"}</span>
                    </>
                  )}
                </div>
                {canCreateTour && (
                  <button
                    data-testid="customer-tours-new-btn"
                    onClick={() => setNewTourOpen(true)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-[12.5px] bg-coach-blue text-white rounded-sm hover:bg-deep-road-navy"
                  >
                    <Plus size={13} /> New Tour
                  </button>
                )}
              </div>
              {tours.length === 0 ? (
                <div className="p-8 text-center text-steel text-[13px]">
                  Nothing here yet. Kick off the first tour when you&apos;re ready — the client is already saved.
                </div>
              ) : (
                <table className="w-full text-[13px]">
                  <thead className="bg-road-white text-[10.5px] uppercase tracking-widest text-steel">
                    <tr>
                      <th className="text-left px-4 py-2">Tour</th>
                      <th className="text-left px-4 py-2 w-32">Status</th>
                      <th className="text-left px-4 py-2 w-52">Dates</th>
                      <th className="w-24" />
                    </tr>
                  </thead>
                  <tbody>
                    {tours.map((t) => (
                      <tr
                        key={t.id}
                        data-testid={`customer-tour-row-${t.id}`}
                        className="border-t border-coach-silver/60 hover:bg-road-white"
                      >
                        <td className="px-4 py-2">
                          <Link to={`/tours/${t.id}`} className="text-carbon hover:text-coach-blue">
                            <span className="font-mono text-[11.5px] text-steel">{t.tour_number}</span>{" "}
                            {t.display_name}
                          </Link>
                        </td>
                        <td className="px-4 py-2 text-steel">{t.status?.replace(/_/g, " ")}</td>
                        <td className="px-4 py-2 text-steel">
                          {t.start_date && t.end_date
                            ? `${t.start_date} → ${t.end_date}`
                            : <span className="italic">Not Received</span>}
                        </td>
                        <td className="px-4 py-2 text-right">
                          <Link to={`/tours/${t.id}`} className="text-coach-blue hover:underline text-[12px]">
                            Open →
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>

          {["quotes", "contracts", "billing"].map((k) => (
            <TabsContent key={k} value={k} className="mt-4">
              {k === "quotes" && <CustomerQuotesPanel customerId={id} tours={tours} />}
              {k === "contracts" && <CustomerContractsPanel customerId={id} tours={tours} />}
              {k === "billing" && <CustomerBillingPanel customerId={id} tours={tours} />}
            </TabsContent>
          ))}
        </Tabs>

        {editOpen && (
          <CustomerFormDialog
            open={editOpen}
            orgs={orgs}
            initial={data}
            onClose={() => setEditOpen(false)}
            onSaved={() => {
              setEditOpen(false);
              load();
            }}
          />
        )}

        {addContactOpen && (
          <ContactFormDialog
            open={addContactOpen}
            orgs={orgs}
            defaultCustomer={data}
            onClose={() => setAddContactOpen(false)}
            onSaved={() => {
              setAddContactOpen(false);
              load();
            }}
          />
        )}

        {newTourOpen && (
          <TourFormDialog
            open={newTourOpen}
            customers={[data]}
            initial={{ customer_id: id, status: "inquiry", artist_display: data.display_name }}
            lockCustomer
            onClose={() => setNewTourOpen(false)}
            onSaved={() => {
              setNewTourOpen(false);
              load();
            }}
          />
        )}
      </PageShell>
    </RoleGate>
  );
}

const Card = ({ title, children }) => (
  <div className="bg-white border border-coach-silver rounded-md p-4">
    <div className="text-[10.5px] uppercase tracking-widest text-steel mb-3">{title}</div>
    {children}
  </div>
);

const OrgRow = ({ label, text, org }) => {
  const value = text || org?.display_name;
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-coach-silver/50 last:border-0">
      <div>
        <div className="text-[11px] uppercase tracking-wide text-steel">{label}</div>
        <div className="text-[13px] text-carbon">{value || <span className="text-steel">—</span>}</div>
      </div>
      {!text && org?.org_type && (
        <span className="text-[10.5px] text-steel uppercase tracking-wide">{org.org_type}</span>
      )}
    </div>
  );
};

const StatRow = ({ k, v, phase, testid }) => (
  <div className="flex items-center justify-between py-1.5 border-b border-coach-silver/50 last:border-0" data-testid={testid}>
    <div className="text-[13px] text-carbon">{k}</div>
    <div className="flex items-center gap-2">
      <span className="text-[13px] text-carbon font-medium">{v}</span>
      {phase && (
        <span className="text-[9.5px] uppercase tracking-widest text-steel">Phase {phase}</span>
      )}
    </div>
  </div>
);

const ContactsPanel = ({ customer, allContacts, onChange, canWrite }) => {
  const [contactId, setContactId] = useState("");
  const [orgId, setOrgId] = useState(customer.display_organization_id || "");
  const [role, setRole] = useState("tour_manager");

  const attach = async () => {
    if (!contactId || !orgId) return toast.error("Contact + Organization required");
    try {
      await contactRoleApi.attach({ contact_id: contactId, organization_id: orgId, role });
      toast.success("Attached");
      setContactId("");
      onChange();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="bg-white border border-coach-silver rounded-md p-4 space-y-4">
      <div className="text-[13px] text-carbon">
        Primary contacts for this customer are derived from linked Organizations
        (display + legal). Attach existing contacts below.
      </div>

      {canWrite && (
        <div className="grid md:grid-cols-4 gap-2">
          <select
            data-testid="ca-contact"
            value={contactId}
            onChange={(e) => setContactId(e.target.value)}
            className="border border-coach-silver rounded-sm px-2 py-1.5 text-[13px]"
          >
            <option value="">— Choose contact —</option>
            {allContacts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.first_name} {c.last_name} {c.primary_email ? `(${c.primary_email})` : ""}
              </option>
            ))}
          </select>
          <select
            data-testid="ca-org"
            value={orgId}
            onChange={(e) => setOrgId(e.target.value)}
            className="border border-coach-silver rounded-sm px-2 py-1.5 text-[13px]"
          >
            <option value="">— Choose organization —</option>
            {[
              customer.display_organization,
              customer.legal_entity_organization,
              customer.billing_organization,
            ]
              .filter(Boolean)
              .map((o) => (
                <option key={o.id} value={o.id}>
                  {o.display_name}
                </option>
              ))}
          </select>
          <select
            data-testid="ca-role"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="border border-coach-silver rounded-sm px-2 py-1.5 text-[13px]"
          >
            {Object.entries(ROLE_LABEL).map(([k, l]) => (
              <option key={k} value={k}>
                {l}
              </option>
            ))}
          </select>
          <button
            data-testid="ca-attach"
            onClick={attach}
            className="bg-coach-blue hover:bg-deep-road-navy text-white text-[12.5px] font-medium px-3 py-1.5 rounded-sm"
          >
            Attach
          </button>
        </div>
      )}

      <div>
        <div className="text-[10.5px] uppercase tracking-widest text-steel mb-2">
          Current Primary Contacts
        </div>
        {(customer.primary_contacts || []).length === 0 && (
          <div className="text-[12px] text-steel">None yet.</div>
        )}
        {(customer.primary_contacts || []).map((c) => (
          <PrimaryContactRow
            key={c.role_id}
            row={c}
            canWrite={canWrite}
            onChange={onChange}
          />
        ))}
      </div>
    </div>
  );
};

// CC1 · Inline Contact Type editor for an existing ContactOrgRole.
// Guarded by `contacts.edit` on the backend; UI shows the pencil only
// when the parent panel has canWrite.
const PrimaryContactRow = ({ row, canWrite, onChange }) => {
  const [editing, setEditing] = useState(false);
  const [role, setRole] = useState(row.role);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!row.role_id || role === row.role) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await contactRoleApi.patch(row.role_id, { role });
      toast.success("Contact type updated");
      setEditing(false);
      onChange();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="flex items-center justify-between py-1.5 border-b border-coach-silver/50 last:border-0 gap-3"
      data-testid={`primary-contact-row-${row.role_id}`}
    >
      <div className="min-w-0 flex-1">
        <div className="text-[13px] text-carbon truncate">
          {row.contact.preferred_name || row.contact.first_name} {row.contact.last_name}
        </div>
        <div className="text-[11.5px] text-steel truncate">
          {row.contact.primary_email || ""}
        </div>
      </div>
      {editing ? (
        <div className="flex items-center gap-2">
          <select
            data-testid={`primary-contact-type-select-${row.role_id}`}
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="border border-coach-silver rounded-sm px-2 py-1 text-[12px]"
          >
            {Object.entries(ROLE_LABEL).map(([k, l]) => (
              <option key={k} value={k}>{l}</option>
            ))}
          </select>
          <button
            data-testid={`primary-contact-type-save-${row.role_id}`}
            onClick={save}
            disabled={saving}
            className="text-[11.5px] font-medium bg-coach-blue hover:bg-deep-road-navy text-white px-2 py-1 rounded-sm disabled:opacity-60"
          >
            {saving ? "…" : "Save"}
          </button>
          <button
            data-testid={`primary-contact-type-cancel-${row.role_id}`}
            onClick={() => { setRole(row.role); setEditing(false); }}
            className="text-[11.5px] text-steel border border-coach-silver px-2 py-1 rounded-sm"
          >
            Cancel
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <span
            data-testid={`primary-contact-type-${row.role_id}`}
            className="text-[11.5px] uppercase tracking-widest text-carbon bg-chrome-mist px-2 py-0.5 rounded-sm"
          >
            {ROLE_LABEL[row.role] || row.role}
          </span>
          {canWrite && (
            <button
              data-testid={`primary-contact-type-edit-${row.role_id}`}
              onClick={() => setEditing(true)}
              className="text-[11.5px] text-coach-blue hover:underline"
              title="Change contact type"
            >
              Edit
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const DocumentsPanel = ({ customerId, docs, onRefresh, canWrite }) => {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [type, setType] = useState("other");
  const [busy, setBusy] = useState(false);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return;
    setBusy(true);
    try {
      await documentApi.upload({
        file,
        title: title || file.name,
        document_type: type,
        entity_type: "customer",
        entity_id: customerId,
      });
      toast.success("Uploaded");
      setFile(null);
      setTitle("");
      onRefresh();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const openDoc = async (doc) => {
    try {
      const d = await documentApi.get(doc.id);
      window.open(documentApi.downloadUrl(d.file_id, d.download_token), "_blank");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="bg-white border border-coach-silver rounded-md p-4 space-y-4">
      {canWrite && (
        <form onSubmit={upload} className="flex flex-wrap gap-2 items-end">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-steel mb-1">File</div>
            <input
              data-testid="doc-file"
              type="file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="text-[12px]"
            />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-steel mb-1">Title</div>
            <input
              data-testid="doc-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="(defaults to filename)"
              className="border border-coach-silver rounded-sm px-2 py-1.5 text-[13px]"
            />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-steel mb-1">Type</div>
            <select
              data-testid="doc-type"
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="border border-coach-silver rounded-sm px-2 py-1.5 text-[13px]"
            >
              {["w9", "coi", "insurance", "invoice", "photo", "other"].map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <button
            data-testid="doc-upload-submit"
            disabled={busy || !file}
            className="bg-coach-blue text-white text-[12.5px] font-medium px-3 py-1.5 rounded-sm disabled:opacity-60"
          >
            {busy ? "Uploading…" : "Upload"}
          </button>
        </form>
      )}
      <table className="w-full text-[13px]">
        <thead className="bg-road-white text-[10.5px] uppercase tracking-widest text-steel">
          <tr>
            <th className="text-left px-3 py-2">Title</th>
            <th className="text-left px-3 py-2 w-32">Type</th>
            <th className="text-left px-3 py-2 w-40">Uploaded</th>
            <th className="w-24" />
          </tr>
        </thead>
        <tbody>
          {docs.map((d) => (
            <tr key={d.id} className="border-t border-coach-silver/60" data-testid={`doc-row-${d.id}`}>
              <td className="px-3 py-2">{d.title}</td>
              <td className="px-3 py-2 text-steel uppercase tracking-wide text-[11px]">{d.document_type}</td>
              <td className="px-3 py-2 text-steel">{d.created_at?.slice(0, 10)}</td>
              <td className="px-3 py-2 text-right">
                <button
                  data-testid={`doc-open-${d.id}`}
                  onClick={() => openDoc(d)}
                  className="text-coach-blue hover:underline"
                >
                  Open
                </button>
              </td>
            </tr>
          ))}
          {docs.length === 0 && (
            <tr>
              <td colSpan={4} className="px-3 py-6 text-center text-steel">
                No documents on this customer yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

const NotesLedgerPanel = ({ customerId, canWrite }) => {
  const [rows, setRows] = useState(null);
  const [meta, setMeta] = useState({ page: 0, page_size: 20, total: 0, has_more: false, has_legacy: false });
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [initialError, setInitialError] = useState(null);
  const [loadMoreError, setLoadMoreError] = useState(null);

  const loadPage = async (page) => {
    const r = await customerApi.notesList(customerId, { page, page_size: 20 });
    return r;
  };

  const loadInitial = async () => {
    setInitialError(null);
    try {
      const r = await loadPage(1);
      setRows(r.results || []);
      setMeta({
        page: r.page, page_size: r.page_size, total: r.total,
        has_more: !!r.has_more, has_legacy: !!r.has_legacy,
      });
    } catch (e) {
      setInitialError(apiError(e));
      toast.error(apiError(e));
      setRows([]);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadInitial(); }, [customerId]);

  const loadMore = async () => {
    if (loadingMore || !meta.has_more) return;
    setLoadMoreError(null);
    setLoadingMore(true);
    try {
      const nextPage = (meta.page || 1) + 1;
      const r = await loadPage(nextPage);
      // Dedup on id (defensive: race with a fresh Add).
      const seen = new Set((rows || []).map((n) => n.id));
      const merged = [...(rows || [])];
      for (const n of (r.results || [])) {
        if (!seen.has(n.id)) merged.push(n);
      }
      setRows(merged);
      setMeta({
        page: r.page, page_size: r.page_size, total: r.total,
        has_more: !!r.has_more, has_legacy: !!r.has_legacy,
      });
    } catch (e) {
      setLoadMoreError(apiError(e));
    } finally {
      setLoadingMore(false);
    }
  };

  const add = async () => {
    const body = draft.trim();
    if (!body) return;
    setSaving(true);
    try {
      const saved = await customerApi.notesAdd(customerId, body);
      // Optimistic prepend — no re-fetch of loaded pages. Dedup on id.
      setRows((prev) => {
        const cur = prev || [];
        if (cur.some((n) => n.id === saved.id)) return cur;
        return [saved, ...cur];
      });
      setMeta((m) => ({ ...m, total: (m.total || 0) + 1 }));
      setDraft("");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const hasAnyRow = Array.isArray(rows) && rows.length > 0;

  return (
    <div className="space-y-3" data-testid="customer-notes-ledger">
      {canWrite && (
        <div className="bg-white border border-coach-silver rounded-md p-3">
          <div className="text-[10.5px] uppercase tracking-widest text-steel mb-2">
            Add note
          </div>
          <textarea
            data-testid="customer-note-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={3}
            placeholder="Type a note…"
            className="w-full border border-coach-silver rounded-sm px-3 py-2 text-[13px] resize-y"
          />
          <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
            <div className="text-[11px] text-steel">
              Notes are timestamped and attributed to you. They cannot be edited or deleted.
            </div>
            <button
              data-testid="customer-note-add-btn"
              onClick={add}
              disabled={saving || !draft.trim()}
              className="bg-coach-blue hover:bg-deep-road-navy text-white text-[12.5px] font-medium px-3 py-1.5 rounded-sm disabled:opacity-60"
            >
              {saving ? "Saving…" : "Add Note"}
            </button>
          </div>
        </div>
      )}

      {rows === null && (
        <div className="p-6 text-center text-steel text-[13px]" data-testid="customer-notes-loading">
          Loading notes…
        </div>
      )}
      {rows !== null && initialError && !hasAnyRow && (
        <div className="bg-white border border-critical-red/40 rounded-md p-4 text-center text-[13px]"
             data-testid="customer-notes-initial-error">
          <div className="text-critical-red mb-2">Notes failed to load.</div>
          <button onClick={loadInitial} data-testid="customer-notes-initial-retry"
                  className="text-coach-blue hover:underline text-[12px]">Retry</button>
        </div>
      )}
      {rows !== null && !initialError && !hasAnyRow && (
        <div
          className="bg-white border border-coach-silver rounded-md p-8 text-center text-steel text-[13px]"
          data-testid="customer-notes-empty"
        >
          No notes yet.
        </div>
      )}
      {hasAnyRow && (
        <>
          <ul className="space-y-2" data-testid="customer-notes-list">
            {rows.map((n) => (
              <li
                key={n.id}
                data-testid={`customer-note-row-${n.id}`}
                className="bg-white border border-coach-silver rounded-md p-3"
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <div className="text-[12px] text-steel">
                    {n.is_legacy ? (
                      <span
                        className="uppercase tracking-widest text-[10.5px] font-medium text-carbon bg-chrome-mist px-1.5 py-0.5 rounded-sm mr-2"
                        data-testid={`customer-note-legacy-tag-${n.id}`}
                        title="Imported from prior Client record"
                      >
                        Legacy Note · Imported from prior Client record
                      </span>
                    ) : (
                      <span
                        className="text-carbon font-medium"
                        data-testid={`customer-note-author-${n.id}`}
                      >
                        {n.author_name || "Author unavailable"}
                      </span>
                    )}
                  </div>
                  <div
                    className="text-[11.5px] text-steel font-mono"
                    data-testid={`customer-note-ts-${n.id}`}
                  >
                    {fmtLedgerTs(n.created_at)}
                  </div>
                </div>
                <div className="mt-1.5 text-[13.5px] text-carbon whitespace-pre-wrap break-words">
                  {n.body}
                </div>
              </li>
            ))}
          </ul>
          {meta.has_more && (
            <div className="pt-2 flex flex-col items-center gap-2">
              {loadMoreError && (
                <div className="text-[11.5px] text-critical-red"
                     data-testid="customer-notes-load-more-error">
                  Could not load more notes.
                </div>
              )}
              <button
                onClick={loadMore}
                disabled={loadingMore}
                data-testid="customer-notes-load-more"
                className="text-coach-blue hover:underline text-[12.5px] disabled:opacity-60"
              >
                {loadingMore ? "Loading…" : (loadMoreError ? "Retry" : `Load more (${Math.max(0, (meta.total || 0) - rows.filter(r => !r.is_legacy).length)} remaining)`)}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const ACTIVITY_LABELS = {
  "customer.created": "Customer created",
  "customer.updated": "Customer updated",
  "customer.note_added": "Note added",
};

const ActivityLedgerPanel = ({ activity, canSeeActivity }) => {
  if (!canSeeActivity) {
    return (
      <div className="bg-white border border-coach-silver rounded-md p-6 text-[13px] text-steel">
        You don&apos;t have permission to view this customer&apos;s activity.
      </div>
    );
  }
  if (!activity || activity.length === 0) {
    return (
      <div
        className="bg-white border border-coach-silver rounded-md p-8 text-center text-steel text-[13px]"
        data-testid="customer-activity-empty"
      >
        No activity yet.
      </div>
    );
  }
  return (
    <ul className="space-y-2" data-testid="customer-activity-list">
      {activity.map((a) => (
        <li
          key={a.id || `${a.action}-${a.ts}`}
          data-testid={`customer-activity-row-${a.id || a.ts}`}
          className="bg-white border border-coach-silver rounded-md p-3"
        >
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <div className="text-[13px] text-carbon font-medium">
              {ACTIVITY_LABELS[a.action] || a.action}
            </div>
            <div
              className="text-[11.5px] text-steel font-mono"
              data-testid={`customer-activity-ts-${a.id || a.ts}`}
            >
              {fmtLedgerTs(a.ts)}
            </div>
          </div>
          <div className="mt-1 text-[12px] text-steel">
            <span data-testid={`customer-activity-actor-${a.id || a.ts}`}>
              {a.actor_name || a.actor_email || "System"}
            </span>
          </div>
        </li>
      ))}
    </ul>
  );
};

// Ledger timestamp — display in America/Chicago per the design spec.
// Falls back to raw string if parsing fails.
const fmtLedgerTs = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  const opts = {
    timeZone: "America/Chicago",
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  };
  return d.toLocaleString("en-US", opts);
};


// ---------- Customer 360 helpers ----------
const fmtMoney = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
};

const fmtDate = (v) => {
  if (!v) return "—";
  if (typeof v === "string") return v.slice(0, 10);
  return String(v);
};

const StatusPill = ({ status }) => {
  const s = (status || "").toLowerCase();
  const map = {
    draft: "bg-chrome-mist text-carbon",
    sent: "bg-coach-blue/15 text-coach-blue",
    accepted: "bg-touring-lime/25 text-carbon",
    declined: "bg-critical-red/15 text-critical-red",
    superseded: "bg-graphite/15 text-steel",
    void: "bg-graphite/15 text-steel",
    generated: "bg-chrome-mist text-carbon",
    awaiting_signature: "bg-amber-hold/20 text-carbon",
    executed: "bg-touring-lime/25 text-carbon",
    scheduled: "bg-chrome-mist text-carbon",
    invoiced: "bg-coach-blue/15 text-coach-blue",
    paid: "bg-touring-lime/25 text-carbon",
    waived: "bg-graphite/15 text-steel",
  };
  return (
    <span
      className={`inline-block text-[10.5px] uppercase tracking-widest px-2 py-0.5 rounded-sm ${map[s] || "bg-chrome-mist text-carbon"}`}
    >
      {s.replace(/_/g, " ") || "—"}
    </span>
  );
};

const QuickStats = ({ tours, rollup }) => {
  const activeStatuses = new Set(["confirmed", "in_progress", "on_tour", "active"]);
  const activeTours = (tours || []).filter((t) => activeStatuses.has((t.status || "").toLowerCase())).length;
  const openQuotes = (rollup.quotes || []).filter((q) =>
    ["draft", "sent"].includes((q.status || "").toLowerCase())
  ).length;
  const executed = (rollup.contracts || []).filter(
    (c) => (c.status || "").toLowerCase() === "executed"
  );
  // Outstanding AR = executed contracts, sum(scheduled_amount - paid_amount) across
  // schedule rows whose status is not paid/waived. Snapshot values only.
  let ar = 0;
  for (const c of executed) {
    for (const row of c.payment_schedule || []) {
      const rs = (row.status || "").toLowerCase();
      if (rs === "paid" || rs === "waived") continue;
      const sched = Number(row.scheduled_amount ?? row.amount ?? 0) || 0;
      const paid = Number(row.paid_amount ?? 0) || 0;
      ar += Math.max(0, sched - paid);
    }
  }
  return (
    <>
      <StatRow k="Active Tours" v={String(activeTours)} testid="stat-active-tours" />
      <StatRow k="Open Quotes" v={rollup.loading ? "…" : String(openQuotes)} testid="stat-open-quotes" />
      <StatRow k="Signed Contracts" v={rollup.loading ? "…" : String(executed.length)} testid="stat-signed-contracts" />
      <StatRow k="Outstanding AR" v={rollup.loading ? "…" : fmtMoney(ar)} testid="stat-outstanding-ar" />
    </>
  );
};

const RollupEmpty = ({ tours, kind }) => {
  const noTours = !tours || tours.length === 0;
  const copy = {
    quotes: {
      noTours: "No quotes yet — this customer has no tours. Create a tour first, then generate a quote from the tour's Pricing & Quote tab.",
      withTours: "No quotes yet for this customer's tours. Open a tour and use the Pricing & Quote tab to generate one.",
    },
    contracts: {
      noTours: "No contracts yet — this customer has no tours. Contracts are generated from an accepted quote inside a tour.",
      withTours: "No contracts yet for this customer's tours. Generate one from an accepted quote inside a tour.",
    },
    billing: {
      noTours: "No billing yet — this customer has no executed contracts. Executed contracts populate the payment schedule.",
      withTours: "No executed contracts yet. Payment schedules appear here once a contract is executed.",
    },
  };
  return (
    <div className="p-8 text-center text-steel text-[13px]" data-testid={`customer-${kind}-empty`}>
      {noTours ? copy[kind].noTours : copy[kind].withTours}
    </div>
  );
};

// ---------- Quotes rollup ----------
const CustomerQuotesPanel = ({ customerId, tours }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (!tours || tours.length === 0) {
      setRows([]);
      return () => {};
    }
    setRows(null);
    Promise.all(
      tours.map((t) =>
        quoteApi.list(t.id).then(
          (rs) => (rs || []).map((r) => ({ ...r, _tour: t })),
          () => []
        )
      )
    ).then((chunks) => {
      if (cancelled) return;
      const all = chunks.flat();
      all.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      setRows(all);
    });
    return () => {
      cancelled = true;
    };
  }, [customerId, tours]);

  if (rows === null) {
    return (
      <div className="bg-white border border-coach-silver rounded-md p-8 text-steel text-[13px]">
        Loading quotes…
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="bg-white border border-coach-silver rounded-md" data-testid="customer-quotes-panel">
        <RollupEmpty tours={tours} kind="quotes" />
      </div>
    );
  }
  return (
    <div className="bg-white border border-coach-silver rounded-md overflow-hidden" data-testid="customer-quotes-panel">
      <table className="w-full text-[13px]">
        <thead className="bg-road-white text-[10.5px] uppercase tracking-widest text-steel">
          <tr>
            <th className="text-left px-4 py-2">Quote</th>
            <th className="text-left px-4 py-2">Tour</th>
            <th className="text-left px-4 py-2 w-32">Status</th>
            <th className="text-right px-4 py-2 w-32">Total</th>
            <th className="text-left px-4 py-2 w-32">Created</th>
            <th className="w-24" />
          </tr>
        </thead>
        <tbody>
          {rows.map((q) => {
            const totals = q.snapshot_pricing?.totals || {};
            const total = totals.customer_total ?? totals.contract_total ?? totals.grand_total ?? totals.total ?? null;
            return (
              <tr
                key={q.id}
                data-testid={`customer-quote-row-${q.id}`}
                className="border-t border-coach-silver/60 hover:bg-road-white"
              >
                <td className="px-4 py-2">
                  <span className="font-mono text-[11.5px] text-steel">v{q.version}</span>
                </td>
                <td className="px-4 py-2">
                  <Link to={`/tours/${q._tour.id}`} className="text-carbon hover:text-coach-blue">
                    <span className="font-mono text-[11.5px] text-steel">{q._tour.tour_number}</span>{" "}
                    {q._tour.display_name}
                  </Link>
                </td>
                <td className="px-4 py-2">
                  <StatusPill status={q.status} />
                </td>
                <td className="px-4 py-2 text-right font-mono text-[12.5px]">{fmtMoney(total)}</td>
                <td className="px-4 py-2 text-steel">{fmtDate(q.created_at)}</td>
                <td className="px-4 py-2 text-right">
                  <Link
                    to={`/tours/${q._tour.id}?tab=pricing`}
                    className="text-coach-blue hover:underline text-[12px]"
                    data-testid={`customer-quote-open-${q.id}`}
                  >
                    Open →
                  </Link>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ---------- Contracts rollup ----------
const CustomerContractsPanel = ({ customerId, tours }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (!tours || tours.length === 0) {
      setRows([]);
      return () => {};
    }
    setRows(null);
    Promise.all(
      tours.map((t) =>
        contractApi.list(t.id).then(
          (rs) => (rs || []).map((r) => ({ ...r, _tour: t })),
          () => []
        )
      )
    ).then((chunks) => {
      if (cancelled) return;
      const all = chunks.flat();
      all.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      setRows(all);
    });
    return () => {
      cancelled = true;
    };
  }, [customerId, tours]);

  if (rows === null) {
    return (
      <div className="bg-white border border-coach-silver rounded-md p-8 text-steel text-[13px]">
        Loading contracts…
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="bg-white border border-coach-silver rounded-md" data-testid="customer-contracts-panel">
        <RollupEmpty tours={tours} kind="contracts" />
      </div>
    );
  }
  return (
    <div className="bg-white border border-coach-silver rounded-md overflow-hidden" data-testid="customer-contracts-panel">
      <table className="w-full text-[13px]">
        <thead className="bg-road-white text-[10.5px] uppercase tracking-widest text-steel">
          <tr>
            <th className="text-left px-4 py-2">Contract</th>
            <th className="text-left px-4 py-2">Tour</th>
            <th className="text-left px-4 py-2 w-40">Status</th>
            <th className="text-right px-4 py-2 w-32">Total</th>
            <th className="text-right px-4 py-2 w-32">Deposit</th>
            <th className="text-right px-4 py-2 w-32">Balance</th>
            <th className="w-24" />
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr
              key={c.id}
              data-testid={`customer-contract-row-${c.id}`}
              className="border-t border-coach-silver/60 hover:bg-road-white"
            >
              <td className="px-4 py-2">
                <span className="font-mono text-[11.5px] text-steel">v{c.version}</span>
              </td>
              <td className="px-4 py-2">
                <Link to={`/tours/${c._tour.id}`} className="text-carbon hover:text-coach-blue">
                  <span className="font-mono text-[11.5px] text-steel">{c._tour.tour_number}</span>{" "}
                  {c._tour.display_name}
                </Link>
              </td>
              <td className="px-4 py-2">
                <StatusPill status={c.status} />
              </td>
              <td className="px-4 py-2 text-right font-mono text-[12.5px]">
                {fmtMoney(c.contract_total)}
              </td>
              <td className="px-4 py-2 text-right font-mono text-[12.5px]">
                {fmtMoney(c.deposit_amount)}
              </td>
              <td className="px-4 py-2 text-right font-mono text-[12.5px]">
                {fmtMoney(c.balance_amount)}
              </td>
              <td className="px-4 py-2 text-right">
                <Link
                  to={`/tours/${c._tour.id}?tab=contracts`}
                  className="text-coach-blue hover:underline text-[12px]"
                  data-testid={`customer-contract-open-${c.id}`}
                >
                  Open →
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ---------- Billing rollup ----------
// Real internal billing surface only. QUICKBOOKS_ENABLED remains OFF —
// this view is driven exclusively by executed-contract payment schedules
// stored in MongoDB.
const CustomerBillingPanel = ({ customerId, tours }) => {
  const [contracts, setContracts] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (!tours || tours.length === 0) {
      setContracts([]);
      return () => {};
    }
    setContracts(null);
    Promise.all(
      tours.map((t) =>
        contractApi.list(t.id).then(
          (rs) => (rs || []).map((r) => ({ ...r, _tour: t })),
          () => []
        )
      )
    ).then((chunks) => {
      if (cancelled) return;
      const all = chunks
        .flat()
        .filter((c) => (c.status || "").toLowerCase() === "executed");
      all.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      setContracts(all);
    });
    return () => {
      cancelled = true;
    };
  }, [customerId, tours]);

  const rows = useMemo(() => {
    if (!contracts) return null;
    const out = [];
    for (const c of contracts) {
      for (const row of c.payment_schedule || []) {
        out.push({ ...row, _contract: c });
      }
    }
    out.sort((a, b) => {
      const ax = a.due_date || "";
      const bx = b.due_date || "";
      return ax.localeCompare(bx);
    });
    return out;
  }, [contracts]);

  const totals = useMemo(() => {
    if (!rows) return { scheduled: 0, paid: 0, outstanding: 0 };
    let scheduled = 0;
    let paid = 0;
    let outstanding = 0;
    for (const r of rows) {
      const sched = Number(r.scheduled_amount ?? r.amount ?? 0) || 0;
      const p = Number(r.paid_amount ?? 0) || 0;
      const rs = (r.status || "").toLowerCase();
      scheduled += sched;
      paid += p;
      if (rs !== "paid" && rs !== "waived") outstanding += Math.max(0, sched - p);
    }
    return { scheduled, paid, outstanding };
  }, [rows]);

  if (rows === null) {
    return (
      <div className="bg-white border border-coach-silver rounded-md p-8 text-steel text-[13px]">
        Loading billing…
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="bg-white border border-coach-silver rounded-md" data-testid="customer-billing-panel">
        <RollupEmpty tours={tours} kind="billing" />
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="customer-billing-panel">
      <div className="grid grid-cols-3 gap-3">
        <BillingKPI label="Scheduled" value={fmtMoney(totals.scheduled)} testid="billing-kpi-scheduled" />
        <BillingKPI label="Paid" value={fmtMoney(totals.paid)} testid="billing-kpi-paid" />
        <BillingKPI label="Outstanding" value={fmtMoney(totals.outstanding)} testid="billing-kpi-outstanding" />
      </div>
      <div className="bg-white border border-coach-silver rounded-md overflow-hidden">
        <table className="w-full text-[13px]">
          <thead className="bg-road-white text-[10.5px] uppercase tracking-widest text-steel">
            <tr>
              <th className="text-left px-4 py-2 w-32">Due</th>
              <th className="text-left px-4 py-2">Row</th>
              <th className="text-left px-4 py-2">Tour · Contract</th>
              <th className="text-left px-4 py-2 w-28">Status</th>
              <th className="text-right px-4 py-2 w-32">Scheduled</th>
              <th className="text-right px-4 py-2 w-32">Paid</th>
              <th className="w-24" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={`${r._contract.id}:${r.id}`}
                data-testid={`customer-billing-row-${r.id}`}
                className="border-t border-coach-silver/60 hover:bg-road-white"
              >
                <td className="px-4 py-2 text-steel font-mono text-[12px]">{fmtDate(r.due_date)}</td>
                <td className="px-4 py-2 text-carbon">{r.label || r.description || "Payment"}</td>
                <td className="px-4 py-2 text-carbon">
                  <Link to={`/tours/${r._contract._tour.id}`} className="hover:text-coach-blue">
                    <span className="font-mono text-[11.5px] text-steel">
                      {r._contract._tour.tour_number}
                    </span>{" "}
                    {r._contract._tour.display_name}
                  </Link>
                  <span className="text-steel text-[11.5px]"> · v{r._contract.version}</span>
                </td>
                <td className="px-4 py-2">
                  <StatusPill status={r.status} />
                </td>
                <td className="px-4 py-2 text-right font-mono text-[12.5px]">
                  {fmtMoney(r.scheduled_amount ?? r.amount)}
                </td>
                <td className="px-4 py-2 text-right font-mono text-[12.5px]">
                  {r.paid_amount ? fmtMoney(r.paid_amount) : <span className="text-steel">—</span>}
                </td>
                <td className="px-4 py-2 text-right">
                  <Link
                    to={`/tours/${r._contract._tour.id}?tab=billing`}
                    className="text-coach-blue hover:underline text-[12px]"
                    data-testid={`customer-billing-open-${r.id}`}
                  >
                    Open →
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const BillingKPI = ({ label, value, testid }) => (
  <div className="bg-white border border-coach-silver rounded-md p-3" data-testid={testid}>
    <div className="text-[10.5px] uppercase tracking-widest text-steel">{label}</div>
    <div className="text-[18px] font-medium text-carbon font-mono mt-1">{value}</div>
  </div>
);
